import { SKYNET_INTERNAL_COMPANY_ID } from 'app/auth';
import { checkIsInternalCompany } from 'app/auth/authUtils';
import _ from 'lodash';

import { PopupDynamicFieldView } from 'constants/formBuilder';
import {
  ActionNameType,
  DefaultDecimalPositions,
  DefaultTemplateElementPosition,
  DefaultWithThousandsSeparator,
  DYNAMIC_FIELD_PATH_SEPARATOR,
  FieldType,
  FormBundleElementType,
  FormApplicationType,
  FormDateFormat,
  FormDynamicFieldSchemaGroupType,
  FormSchemaType,
  FormType,
  FormTypeCompatibilityEnum,
  FormTypeNewApi,
  GeographicalState,
  MAX_DECIMAL_POSITIONS,
  MAX_MULTIPLIER,
  OverrideBadge,
  PrismEditorBlockReason,
  SCRIBAN_MARKER,
  TAG_ASSIGNED_DATE_DYNAMIC_FIELD,
  TAG_NAME_DYNAMIC_FIELD,
} from 'constants/forms';
import {
  DeliveryStatus,
  FormDeliveryAcknowledgmentRule,
  FormDeliveryChannelType,
  FormDeliveryReadinessStatus,
  FormDeliveryRecipientType,
  FormDeliveryStatusFamily,
  NewFormDeliveryStatus,
  NewFormDeliveryStatusToOldMap,
  PhysicalMailDeliveryStatus,
  RecipientRequestType,
  SignatureRequestStatus,
} from 'constants/loanApplication';
import { formPurposeConfigByType, statesOptions } from 'constants/options';
import { nationalCategory } from 'constants/states';
import { logger } from 'utils/logger';

import { getPersonFullName } from './loanApplication';

const NEW_Y = 10;

export const isPdfFormType = formType => [FormType.PDF, FormType.CUSTOM].includes(formType);

export const getIsFormNonOverridable = ({ formType, isCustom }) =>
  formType === FormType.CUSTOM || !!isCustom;

export const getPrismEditorBlockReason = ({ template, isSeededSystemForm }) => {
  if (isSeededSystemForm) {
    return PrismEditorBlockReason.SEEDED_SYSTEM_FORM;
  }

  if (_.isString(template) && template.includes(SCRIBAN_MARKER)) {
    return PrismEditorBlockReason.SCRIBAN;
  }

  return null;
};

export const isDecimalPositionsAllowed = ({ floatValue }) =>
  _.isNil(floatValue) || (floatValue >= 0 && floatValue <= MAX_DECIMAL_POSITIONS);

export const isMultiplierAllowed = ({ floatValue }) =>
  _.isNil(floatValue) || (floatValue >= 0 && floatValue <= MAX_MULTIPLIER);

export const getNextViewId = ({ type, ignoreUserDefinedStyles }) => {
  if ([FormSchemaType.TODAY_X_DAYS, FormSchemaType.DATE_X_BUSINESS_DAYS].includes(type)) {
    return PopupDynamicFieldView.TODAYS_DATE_PLUS_NUMBER;
  }

  if ([FormSchemaType.RATE, FormSchemaType.CURRENCY].includes(type)) {
    return PopupDynamicFieldView.RATE_CURRENCY_OPTIONS;
  }

  if (type === FormSchemaType.HAND_WRITING_SIGNATURE) {
    return PopupDynamicFieldView.HAND_WRITING_SIGNATURE_OPTIONS;
  }

  if (type === FormSchemaType.NOTARY_MULTILINE_TEXT) {
    return PopupDynamicFieldView.NOTARY_OPTIONS_VIEW;
  }

  if (_.isNull(type) || ignoreUserDefinedStyles) {
    return PopupDynamicFieldView.FIELDS_LIST_VIEW;
  }

  return PopupDynamicFieldView.STYLE_OPTIONS_VIEW;
};

export const getActionProperty = ({
  dataDynamicType,
  addDays,
  includeSign,
  decimalPositions,
  withThousandsSeparator,
  isDateOnly,
  checkboxInverseValue,
  excludeDate,
  excludeSignatureLine,
  isSpelledOut,
}) => {
  const formatDate = isDateOnly ? FormDateFormat.DATE_ONLY : FormDateFormat.DATE_WITH_TIME;

  if (FormSchemaType.DATE_X_BUSINESS_DAYS === dataDynamicType) {
    return {
      name: ActionNameType.ADD_BUSINESS_DAYS_WITH_FORMAT,
      args: [addDays, formatDate],
    };
  }

  if (FormSchemaType.TODAY_X_DAYS === dataDynamicType) {
    return {
      name: ActionNameType.ADD_DAYS_WITH_FORMAT,
      args: [addDays, formatDate],
    };
  }

  if (FormSchemaType.RATE === dataDynamicType) {
    return {
      name: ActionNameType.RATE_PERCENTAGE_FORMAT,
      args: [includeSign, ...(!_.isNil(decimalPositions) ? [decimalPositions] : [])],
    };
  }

  if (FormSchemaType.CURRENCY === dataDynamicType) {
    return {
      name: ActionNameType.CURRENCY_SIGN_FORMAT,
      args: [includeSign, ...(!_.isNil(decimalPositions) ? [decimalPositions] : [])],
    };
  }

  if (FormSchemaType.HAND_WRITING_SIGNATURE === dataDynamicType) {
    return {
      name: ActionNameType.FORMAT_SIGNATURE,
      args: [excludeSignatureLine, excludeDate],
    };
  }

  if (FormSchemaType.PERCENT === dataDynamicType) {
    return {
      name: ActionNameType.PERCENT_PERCENTAGE_FORMAT,
      args: [includeSign, ...(!_.isNil(decimalPositions) ? [decimalPositions] : [])],
    };
  }

  if (
    [FormSchemaType.INT, FormSchemaType.DOUBLE, FormSchemaType.LONG].includes(dataDynamicType) &&
    !_.isNil(decimalPositions)
  ) {
    const resolvedThousandsSeparator = _.isNil(withThousandsSeparator)
      ? (DefaultWithThousandsSeparator[dataDynamicType] ?? false)
      : withThousandsSeparator;

    return {
      name: ActionNameType.FORMAT_NUMERIC,
      args: [decimalPositions, resolvedThousandsSeparator],
    };
  }

  if (FormSchemaType.DATE === dataDynamicType) {
    return {
      name: ActionNameType.FORMAT_DATE,
      args: [formatDate],
    };
  }

  if (FormSchemaType.BOOL === dataDynamicType && checkboxInverseValue) {
    return {
      name: ActionNameType.INVERSE_BOOL_VALUE,
      args: [],
    };
  }

  if (FormSchemaType.STRING === dataDynamicType && isSpelledOut) {
    return {
      name: ActionNameType.STATE_SPELLED_OUT,
      args: [],
    };
  }

  return null;
};

const readThousandsSeparator = action => {
  const secondArg = action?.args?.[1];

  return _.isBoolean(secondArg) ? secondArg : undefined;
};

export const getWithThousandsSeparatorFromAction = (action, type, schemaDefaultAction) =>
  readThousandsSeparator(action) ??
  readThousandsSeparator(schemaDefaultAction) ??
  DefaultWithThousandsSeparator[type] ??
  false;

const readDecimalPositions = (action, type) => {
  const args = action?.args;

  if (!args?.length) {
    return undefined;
  }

  if ([FormSchemaType.INT, FormSchemaType.DOUBLE, FormSchemaType.LONG].includes(type)) {
    return _.isNumber(args[0]) ? args[0] : undefined;
  }

  const secondArg = args[1];

  if (_.isNumber(secondArg)) {
    return secondArg;
  }

  if (type === FormSchemaType.CURRENCY && _.isBoolean(secondArg)) {
    return secondArg ? DefaultDecimalPositions[FormSchemaType.CURRENCY] : 0;
  }

  return undefined;
};

const clampDecimalPositions = value =>
  _.isNumber(value) && value >= 0 && value <= MAX_DECIMAL_POSITIONS ? value : undefined;

// Seeding from the schema-published default keeps server-side per-field overrides in sync with the
// editor; DefaultDecimalPositions is the last resort for fields the schema publishes no default for.
export const getFieldDecimalPositionsDefault = (schemaDefaultAction, type) =>
  clampDecimalPositions(readDecimalPositions(schemaDefaultAction, type)) ??
  DefaultDecimalPositions[type] ??
  0;

export const getDecimalPositionsFromAction = (action, type, schemaDefaultAction) =>
  readDecimalPositions(action, type) ?? getFieldDecimalPositionsDefault(schemaDefaultAction, type);

export const flatDynamicFields = dynamicFieldsSchema => {
  return dynamicFieldsSchema.reduce(function iterator(acc, { fields, categories }) {
    fields.forEach(field => {
      acc[field.value] = field;
    });

    if (categories.length) {
      categories.forEach(category => iterator(acc, category));
    }

    return acc;
  }, {});
};

const findDynamicFieldPath = (categories, value, normalizedValue, parents) => {
  return categories.reduce((found, category) => {
    if (found) {
      return found;
    }

    const nextParents = [...parents, category.title];

    const matchedField = category.fields?.find(
      field =>
        field.value === value ||
        _.toLower(field.value) === normalizedValue ||
        _.toUpper(field.value) === value,
    );

    if (matchedField) {
      return [...nextParents, matchedField.label || matchedField.title];
    }

    if (category.categories?.length) {
      return findDynamicFieldPath(category.categories, value, normalizedValue, nextParents);
    }

    return null;
  }, null);
};

export const getDynamicFieldPath = ({ dynamicFieldsSchema, value, title, label }) => {
  if (!dynamicFieldsSchema?.length || !value) {
    return [];
  }

  const normalizedValue = _.toLower(value);
  const path = findDynamicFieldPath(dynamicFieldsSchema, value, normalizedValue, []);

  if (path?.length) {
    return path;
  }

  const fallbackLabel = label || title;

  return fallbackLabel ? [fallbackLabel] : [];
};

export const getDuplicateDynamicFieldNames = dynamicFieldsSchema => {
  if (!dynamicFieldsSchema?.length) {
    return new Set();
  }

  const fieldsByValue = flatDynamicFields(dynamicFieldsSchema);
  const counts = Object.values(fieldsByValue).reduce((acc, field) => {
    const name = field.label || field.title;

    if (!name) {
      return acc;
    }

    acc[name] = (acc[name] ?? 0) + 1;

    return acc;
  }, {});

  return new Set(Object.keys(counts).filter(name => counts[name] > 1));
};

export const getDynamicFieldPathSuffix = ({ dynamicFieldsSchema, value }) => {
  const path = getDynamicFieldPath({ dynamicFieldsSchema, value });
  const parents = path.slice(0, -1);

  return parents.length ? ` (${parents.join(DYNAMIC_FIELD_PATH_SEPARATOR)})` : '';
};

export const getDynamicFieldCatalogName = ({ dynamicFieldsNamesById, value }) =>
  dynamicFieldsNamesById?.[value?.toLowerCase()] || dynamicFieldsNamesById?.[value?.toUpperCase()];

export const getDynamicFieldDisplayNameWithPath = ({
  dynamicFieldsSchema,
  duplicateDynamicFieldNames,
  value,
  displayName,
}) => {
  if (!displayName || !duplicateDynamicFieldNames?.has(displayName)) {
    return displayName;
  }

  const suffix = getDynamicFieldPathSuffix({ dynamicFieldsSchema, value });

  return suffix ? `${displayName}${suffix}` : displayName;
};

export const flatDynamicFieldsToValueName = dynamicFieldsSchema => {
  const flattenDynamicValues = flatDynamicFields(dynamicFieldsSchema);

  return Object.entries(flattenDynamicValues).reduce((acc, [key, { title, label }]) => {
    acc[key] = label || title;

    return acc;
  }, {});
};

export const sortDynamicFieldsSchema = (dynamicFieldsSchema, normalizeFields) => {
  if (!dynamicFieldsSchema?.length) {
    return [];
  }

  const root = 'root';
  const title = 'title';

  const schemaMapper = (acc, group, path = root) => {
    _.set(
      acc,
      path,
      _.sortBy(group, title).map(item => ({ ...item, uniqueId: _.uniqueId() })),
    );

    _.get(acc, path)?.forEach((entity, index) => {
      const { fields, categories } = entity;

      const currentPath = `${path}[${index}].`;

      if (fields?.length) {
        if (normalizeFields) {
          fields.forEach(normalizeFields);
        }

        schemaMapper(acc, fields, currentPath + FormDynamicFieldSchemaGroupType.FIELDS);
      }

      if (categories?.length) {
        schemaMapper(acc, categories, currentPath + FormDynamicFieldSchemaGroupType.CATEGORIES);
      }
    });

    return acc;
  };

  const schema = schemaMapper({}, dynamicFieldsSchema);

  return schema[root];
};

export const checkIsNewBoolPdfDynamicField = (element, formType) =>
  element.position?.isNewItem &&
  isPdfFormType(formType) &&
  element[FieldType.DYNAMIC]?.type === FormSchemaType.BOOL;

export const convertFormsToListOptions = forms =>
  forms.map(({ name, formId, companyId, state, formType }) => ({
    name,
    value: formId,
    state,
    formType,
    isGlobal: checkIsInternalCompany({ companyId }),
  }));

export const getFormattedFormInfo = ({
  id,
  name,
  geographicalState,
  templateType,
  isEditable,
  isGlobal,
}) => ({
  formId: id,
  name,
  state: geographicalState?.value
    ? geographicalState.value?.abbreviation
    : _.toLower(geographicalState?.type),
  formType: isEditable ? FormTypeCompatibilityEnum[templateType] : FormType.CUSTOM,
  companyId: isGlobal && SKYNET_INTERNAL_COMPANY_ID,
});

export const getFormTemplateType = form => form.templateType ?? form.template?.type;

export const getFormattedDataFormsNewApi = ({
  isGlobal,
  documentTypeId,
  id,
  geographicalState,
  lifeTimeInfo,
  name,
  version,
  type,
  ...data
}) => {
  const formType = data.isEditable
    ? FormTypeCompatibilityEnum[getFormTemplateType(data)]
    : FormType.CUSTOM;
  const formApplicationType = formApplicationTypeConverter(type);

  return {
    isGlobal,
    documentTypeId,
    formId: id,
    formType,
    state: geographicalState?.value
      ? geographicalState.value?.abbreviation
      : _.toLower(geographicalState?.type),
    lastModified: lifeTimeInfo?.modifiedDate || lifeTimeInfo?.createdDate,
    modifiedByUserId: lifeTimeInfo?.modifiedByUserId || lifeTimeInfo?.createdByUserId,
    createdByUserId: lifeTimeInfo?.createdByUserId,
    createdDate: lifeTimeInfo?.createdDate,
    name,
    version,
    companyId: isGlobal && SKYNET_INTERNAL_COMPANY_ID,
    formApplicationType,
    withPageNumbers: data.withPageNumbers,
    disclosureType: data.disclosureType || null,
    formPurpose: data.formPurpose || null,
    purposeType: data.purposeType || null,
    hasLocalOverride: data.hasLocalOverride || false,
    isCustom: data.isCustom || false,
    isSeededSystemForm: data.isSeededSystemForm || false,
    localOverride: data.localOverride || null,
  };
};

const EMPTY_FORM_PURPOSE_OPTIONS = [];

export const getFormPurposeOptions = purposeType =>
  formPurposeConfigByType[purposeType]?.options || EMPTY_FORM_PURPOSE_OPTIONS;

export const getFormPurposeSelectLabel = purposeType =>
  formPurposeConfigByType[purposeType]?.label || '';

// Forms created before the two-part purpose model (FH-2744) persist a FormPurpose option with a NULL
// PurposeType. Derive the type from whichever option list owns the value so the edit modal can rehydrate
// the dependent select for those legacy rows.
// TODO(FH-2744): remove this helper once the PurposeType backfill migration has populated every legacy
// row — the API will then always return purposeType and the client-side fallback is dead code.
export const getFormPurposeTypeByPurpose = formPurpose => {
  if (!formPurpose) {
    return null;
  }

  const matchedType = Object.keys(formPurposeConfigByType).find(purposeType =>
    getFormPurposeOptions(purposeType).some(({ value }) => value === formPurpose),
  );

  return matchedType || null;
};

export const getGeographicalState = ({ state }) => {
  const isNational = nationalCategory.includes(state);
  const type = isNational ? GeographicalState.NATIONAL : GeographicalState.US;
  const value = isNational
    ? undefined
    : { abbreviation: statesOptions.find(({ value }) => value === state)?.value };

  return { type, value };
};

export const formApplicationTypeConverter = value => {
  switch (value) {
    case FormApplicationType.LOAN_APPLICATION:
      return FormTypeNewApi.BORROWER;

    case FormApplicationType.BROKER_APPLICATION:
      return FormTypeNewApi.BROKER;

    case FormTypeNewApi.BORROWER:
      return FormApplicationType.LOAN_APPLICATION;

    case FormTypeNewApi.BROKER:
      return FormApplicationType.BROKER_APPLICATION;

    default:
      return;
  }
};

export const checkIsTagName = title => title?.includes(TAG_NAME_DYNAMIC_FIELD);
export const checkIsTagAssignedDate = title => title?.includes(TAG_ASSIGNED_DATE_DYNAMIC_FIELD);

export const getFormattedFormBundleData = ({
  elements,
  lifeTimeInfo,
  id,
  behaviorOptions,
  isGlobal,
  ...data
}) => {
  const forms = elements
    ?.filter(({ elementType }) => elementType !== FormBundleElementType.DOCUMENT_TYPE)
    .map(({ id, formName, formId }) => ({
      formId: id,
      name: formName,
      formElementId: formId,
    }));

  return {
    forms,
    lastModified: lifeTimeInfo.modifiedDate ?? lifeTimeInfo.createdDate,
    formBundleId: id,
    canBeDelayed: behaviorOptions.canBeDelayed,
    companyId: isGlobal && SKYNET_INTERNAL_COMPANY_ID,
    ...data,
  };
};

export const getFormattedFormBundleFormData = ({
  id,
  formBundleId,
  formId = null,
  formName = null,
  elementType = FormBundleElementType.FORM,
  documentTypeId = null,
  documentVersionType = null,
  lifeTimeInfo,
  orderNumber,
  isGlobal,
  expression,
}) => {
  const { createdByUserId, modifiedByUserId, createdDate, modifiedDate } = lifeTimeInfo;

  return {
    formId,
    formName,
    elementType,
    documentTypeId,
    documentVersionType,
    formBundleId,
    createdByUserId,
    modifiedByUserId: modifiedByUserId || createdByUserId,
    createdDate,
    lastModified: modifiedDate || createdDate,
    orderNumber,
    formBundleFormId: id,
    companyId: isGlobal && SKYNET_INTERNAL_COMPANY_ID,
    expression: expression.value || null,
  };
};

export const getFormBundleRequestData = ({ form, formBundle, acknowledgementRule }) => ({
  name: form.name,
  documentTypeId: form.documentType.value,
  isDraft: form.isDraft,
  behaviorOptions: {
    isAvailableForManualSend: false,
    canBeDelayed: form.canBeDelayed,
  },
  acknowledgementRule,
  shouldBeSigned: form.shouldBeSigned,
  withPageNumbers: form.withPageNumbers,
  ...(formBundle && { id: formBundle.formBundleId }),
});

export const getOverrideBadge = ({ isGlobal, hasLocalOverride }) => {
  if (!isGlobal) {
    return OverrideBadge.LOCAL;
  }

  return hasLocalOverride ? OverrideBadge.GLOBAL_WITH_OVERRIDES : OverrideBadge.GLOBAL;
};

export const getFormBundleOverrideRequestData = ({ form, formBundle, acknowledgementRule }) => ({
  formBundleId: formBundle.formBundleId,
  isDraft: form.isDraft,
  shouldBeSigned: form.shouldBeSigned,
  behaviorOptions: {
    isAvailableForManualSend: false,
    canBeDelayed: form.canBeDelayed,
  },
  acknowledgementRule,
  withPageNumbers: form.withPageNumbers,
});

// Skynet.Forms serializes a PhysicalMail recipient request's progress as a polymorphic VO
// (`IFormDeliveryRecipientRequestStatus.IPhysicalMail`) whose `type` discriminator is the VO's own
// class name (`InTransitByMail`, `DeliveredByMail`, …) — NOT the `FormDeliveryMailStatus` enum
// spelling (`InTransit`, `Delivered`). Normalize to `PhysicalMailDeliveryStatus` here so the rest of
// the FE (status chips, tooltips) never has to know the VO's internal naming.
const PHYSICAL_MAIL_STATUS_TYPE_TO_STATUS = {
  AwaitingMailDispatch: PhysicalMailDeliveryStatus.AWAITING_DISPATCH,
  Mailed: PhysicalMailDeliveryStatus.MAILED,
  InTransitByMail: PhysicalMailDeliveryStatus.IN_TRANSIT,
  DeliveredByMail: PhysicalMailDeliveryStatus.DELIVERED,
  ReturnedToSender: PhysicalMailDeliveryStatus.RETURNED_TO_SENDER,
  MailFailed: PhysicalMailDeliveryStatus.MAIL_FAILED,
};

// Each physical-mail status VO carries its "as-of" date under a different property name — pick the
// right one instead of assuming a single flat `mailStatusDate`.
const PHYSICAL_MAIL_STATUS_DATE_FIELD = {
  AwaitingMailDispatch: 'createdDate',
  Mailed: 'mailedDate',
  InTransitByMail: 'inTransitDate',
  DeliveredByMail: 'deliveredDate',
  ReturnedToSender: 'returnedDate',
  MailFailed: 'failedDate',
};

const getPhysicalMailStatusDate = status =>
  status?.[PHYSICAL_MAIL_STATUS_DATE_FIELD[status?.type]] ?? null;

const getStatusFamily = statusType => statusType?.split('.')[0];

// Skynet.Forms serializes CancelledBySystem on recipients only; it is accepted on the package lane
// too because the cost of the guess being wrong is asymmetric — an unrecognized cancelled package
// would render as live and re-enable Resend.
const CANCELLED_STATUS_FAMILIES = [
  FormDeliveryStatusFamily.CANCELLED,
  FormDeliveryStatusFamily.CANCELLED_BY_SYSTEM,
];

const COMPLETED_ON_SIGNING_PREFIX = 'Completed.OnSigning';

// Every family the request lane can serialize, per the JsonSubtypes registrations on
// IFormDeliveryRequestStatus (Skynet.Forms). IScheduled's own Ready | Transformed resolve through
// the Scheduled branch below.
const PACKAGE_STATUS_NAME_BY_FAMILY = {
  [FormDeliveryStatusFamily.CANCELLED]: SignatureRequestStatus.CANCELED,
  [FormDeliveryStatusFamily.CANCELLED_BY_SYSTEM]: SignatureRequestStatus.CANCELED,
  [FormDeliveryStatusFamily.IN_PROGRESS]: SignatureRequestStatus.BORROWERS_PENDING,
};

// The Completed family is resolved by discriminator, not collapsed: only the on-signing arms
// represent a collected signature. Completing on sending or acknowledgment is a delivery, and
// COMPLETED is what getIsESigned and SIGNED_PACKAGE_STATUSES both read as "e-signed".
const getCompletedStatusName = statusType =>
  statusType.startsWith(COMPLETED_ON_SIGNING_PREFIX)
    ? SignatureRequestStatus.COMPLETED
    : SignatureRequestStatus.DELIVERED;

// Cancellation outranks the scheduled lane: a package cancelled before its scheduled generation
// (`Cancelled.AfterScheduling`) is no longer pending.
const getPackageStatusName = ({ type, statusType }) => {
  const family = getStatusFamily(statusType);
  const familyStatusName = PACKAGE_STATUS_NAME_BY_FAMILY[family];

  if (familyStatusName) {
    return familyStatusName;
  }

  if (family === FormDeliveryStatusFamily.COMPLETED) {
    return getCompletedStatusName(statusType);
  }

  if (type === NewFormDeliveryStatus.Scheduled) {
    return SignatureRequestStatus.SCHEDULED;
  }

  // A family added backend-side without a mapping here paints the neutral placeholder, which is
  // silent by design. Logged at error so it survives the production logging level and reaches the
  // aggregator — a warn is dropped by both.
  logger.error(`[FormDelivery] unmapped package status family: ${statusType}`);

  return statusType;
};

const getFormattedRecipientsRequests = ({
  recipientRequests,
  acknowledgedDate,
  acknowledgmentRule,
  // The owning package/request status — a hard-failed physical-mail send is recorded by cancelling
  // the FormDeliveryRequest (DEV-4795), but the recipient's own PhysicalMail status VO has no
  // Cancelled branch (CancelRecipientRequest is a no-op for physical mail), so a recipient stuck at
  // AwaitingMailDispatch under a Cancelled package would otherwise render a stale "Awaiting dispatch"
  // chip for a send that actually failed. Fold the package's Cancelled state down onto that recipient.
  packageStatus,
} = {}) =>
  recipientRequests?.map(
    ({
      status,
      id,
      recipient,
      type,
      mailClass,
      dispatchProviderKey,
      dispatchReference,
      requiresRemediation,
      documentId,
      isFenced,
    }) => {
      const isRecipientRequestTypeNonSignable = type === RecipientRequestType.NonSignable;
      const isRecipientRequestTypePhysicalMail = type === RecipientRequestType.PhysicalMail;

      const isStaleAwaitingDispatch =
        isRecipientRequestTypePhysicalMail &&
        status?.type === 'AwaitingMailDispatch' &&
        packageStatus === SignatureRequestStatus.CANCELED;

      // FH-1394: an email/portal recipient is dispatched only once its status carries a SentDate. Until then the
      // status VO is a placeholder SentToRecipient(null), so the row must read "Not sent yet" rather than "Sent".
      const isNotYetSent = !isRecipientRequestTypePhysicalMail && _.isNil(status?.sentDate);

      const getDeliveryStatus = () => {
        if (isStaleAwaitingDispatch) {
          return DeliveryStatus.CANCELED;
        }

        // Resolved before the sent-date and channel branches: a cancelled recipient may carry a null
        // sentDate, which would otherwise read as the FH-1394 "Not sent yet" placeholder.
        if (CANCELLED_STATUS_FAMILIES.includes(getStatusFamily(status?.type))) {
          return DeliveryStatus.CANCELED;
        }

        if (isRecipientRequestTypePhysicalMail) {
          return PHYSICAL_MAIL_STATUS_TYPE_TO_STATUS[status.type] ?? status.type;
        }

        if (isNotYetSent) {
          return DeliveryStatus.NOT_SENT;
        }

        if (isRecipientRequestTypeNonSignable) {
          return DeliveryStatus.SENT;
        }

        return NewFormDeliveryStatusToOldMap[status.type] || status.type;
      };

      const deliveryStatus = getDeliveryStatus();

      const shouldAcknowledgeByAllRecipients =
        acknowledgmentRule.type ===
        FormDeliveryAcknowledgmentRule.MANDATORY_ON_READING_BY_ALL_BORROWERS;
      const isPrimaryBorrowerRecipient =
        recipient.type === FormDeliveryRecipientType.PRIMARY_BORROWER;
      const recipientAcknowledgedDate =
        shouldAcknowledgeByAllRecipients || isPrimaryBorrowerRecipient
          ? acknowledgedDate
          : undefined;

      return {
        acknowledgedDate: recipientAcknowledgedDate,
        channel: isRecipientRequestTypePhysicalMail
          ? FormDeliveryChannelType.MAIL
          : FormDeliveryChannelType.EMAIL,
        consentedDate: status.readDate,
        dispatchProviderKey: isRecipientRequestTypePhysicalMail
          ? (dispatchProviderKey ?? null)
          : null,
        dispatchReference: isRecipientRequestTypePhysicalMail ? (dispatchReference ?? null) : null,
        documentId: documentId ?? null,
        formBundleSignatureRequestItemId: id,
        isFenced: isFenced === true,
        mailClass: isRecipientRequestTypePhysicalMail ? (mailClass ?? null) : null,
        mailFailureReason:
          isRecipientRequestTypePhysicalMail && status?.type === 'MailFailed'
            ? (status.failureReason ?? null)
            : null,
        mailStatusDate: isRecipientRequestTypePhysicalMail
          ? getPhysicalMailStatusDate(status)
          : null,
        openedDate: status.notificationOpenedDate,
        // CancelRecipientRequest is a no-op for physical mail, and a package cancelled before
        // dispatch leaves its recipients un-cancelled, so the row's own status is not a reliable
        // proxy for the package's.
        packageStatus,
        recipientType: recipient.type,
        requiresRemediation: isRecipientRequestTypePhysicalMail ? !!requiresRemediation : false,
        sentDate: status.sentDate,
        signedDate: status.signedDate,
        signerEmail: recipient.email,
        signerFirstName: recipient.firstName,
        signerFullName: getPersonFullName(recipient),
        signerLastName: recipient.lastName,
        status: deliveryStatus,
      };
    },
  );

export const getFormattedFormDeliveryRequestsData = formDeliveryRequests => {
  return formDeliveryRequests.map(
    ({
      formBundleId,
      type,
      status,
      recipientRequests,
      applicationId,
      documentId,
      id,
      acknowledgmentRule,
      readinessStatus,
      sentDate,
      isGeneratedOutside,
    }) => {
      const isSignable = type === NewFormDeliveryStatus.Signable;
      const createdDate = status.sentDate || status.completedDate;
      const { acknowledgedDate } = status;
      const statusName = getPackageStatusName({ type, statusType: status.type });

      const signatureItems = getFormattedRecipientsRequests({
        recipientRequests,
        acknowledgedDate,
        acknowledgmentRule,
        packageStatus: statusName,
      });

      return {
        acknowledgedDate,
        formBundleId,
        isSignable,
        // Kept off the request type rather than the derived status: a package cancelled before its
        // scheduled generation resolves to Canceled, and the scheduled body must still render what
        // was scheduled instead of an empty signer table.
        isScheduledDelivery: type === NewFormDeliveryStatus.Scheduled,
        status: statusName,
        createdDate,
        signatureItems,
        applicationId,
        documentId,
        formBundleSignatureRequestId: id,
        scheduledDeliveryDate: status.scheduledTransformationDate,
        readinessStatus: readinessStatus ?? FormDeliveryReadinessStatus.APPROVED,
        sentDate: sentDate ?? null,
        isGeneratedOutside: !!isGeneratedOutside,
      };
    },
  );
};

export const getNewPdfElementPosition = elementIndex => {
  const { height, ...elementPosition } = DefaultTemplateElementPosition[FieldType.MULTIPLE];

  const newY = (elementIndex + 1) * height + NEW_Y;

  return { y: newY, height, ...elementPosition };
};
